import { useState } from "react";
import { format } from "date-fns";
import { ChevronDownIcon, ChevronUpIcon, EnvelopeIcon } from "@heroicons/react/20/solid";
import type { AssociatedObject } from "./types";
import { isEmailMetadata } from "./types";

interface EmailCardProps {
  email: AssociatedObject;
}

const EmailCard = ({ email }: EmailCardProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isEmailMetadata(email.meta_data)) {
    return null;
  }

  const metadata = email.meta_data;
  const { subject, sender, recipients, timestamp, content, labels } = metadata;

  const formattedDate = timestamp ? format(new Date(timestamp), "MMM d, yyyy 'at' h:mm a") : "";
  const contentPreview = content?.length > 200 ? `${content.substring(0, 200)}...` : content;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-grow min-w-0">
          <h3 className="text-base font-semibold text-gray-900 truncate">
            {subject || "(No Subject)"}
          </h3>
        </div>
        <div className="flex-shrink-0">
          <EnvelopeIcon className="h-5 w-5 text-gray-400" />
        </div>
      </div>

      {/* Meta info */}
      <div className="text-sm text-gray-600 mb-3">
        <div className="flex items-center gap-2">
          <span className="font-medium">From:</span>
          <span className="truncate">{sender}</span>
        </div>
        {formattedDate && (
          <div className="text-xs text-gray-500 mt-1">{formattedDate}</div>
        )}
      </div>

      {/* Content Preview */}
      {content && (
        <div className="text-sm text-gray-700 mb-3">
          {isExpanded ? (
            <div className="whitespace-pre-wrap">{content}</div>
          ) : (
            <div className="line-clamp-3">{contentPreview}</div>
          )}
        </div>
      )}

      {/* Labels */}
      {labels && labels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {labels.map((label) => (
            <span
              key={label}
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
            >
              {label}
            </span>
          ))}
        </div>
      )}

      {/* Relations (Recipients) */}
      {recipients && recipients.length > 0 && (
        <div className="border-t border-gray-100 pt-3 mt-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Relations:</span>
            <div className="flex flex-wrap gap-2">
              {recipients.slice(0, 3).map((recipient) => (
                <div key={recipient} className="flex items-center gap-1">
                  <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium text-white">
                    {recipient.charAt(0).toUpperCase()}
                  </div>
                  <span className="text-xs text-gray-700 truncate max-w-[120px]">
                    {recipient}
                  </span>
                </div>
              ))}
              {recipients.length > 3 && (
                <span className="text-xs text-gray-500">
                  +{recipients.length - 3} more
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Expand/Collapse button */}
      {content && content.length > 200 && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-3 flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-medium"
        >
          {isExpanded ? (
            <>
              <ChevronUpIcon className="h-4 w-4" />
              Show less
            </>
          ) : (
            <>
              <ChevronDownIcon className="h-4 w-4" />
              Show more
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default EmailCard;
